import { ErrorMessage, Field, Form, Formik } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { object, number, string, boolean, mixed, array } from "yup";
import { useSearchParams } from "react-router-dom";

import { addBillValue } from "../BillingForm";
import DropDown from "app/components/commonComponent/dropDown/DropDown";
import { convert, getSearchParameter } from "app/helpers/helperFunction";
import { PAYMENT_STATUS } from "app/helpers/constants";
import {
  ResetButton,
  SubmitButton,
} from "app/components/commonComponent/buttons/Buttons";
import { masterDataModal } from "app/reducers/masterData/masterDataReducer";
import { useSendDataChangeWarning } from "app/helpers/webSocketHelper";
import { addReimbursementInvoice } from "app/reducers/reimbursement/reimbursementAction";
import { connect } from "react-redux";
import ConfirmChangePopup from "../../task/component/ConfirmChangePopup";

export interface clientData {
  ClientId: number;
  ClientName: string;
}
interface props {
  initialValue: addBillValue;
  billDispatch: Function;
  title: string;
  masterDataList: {
    loading: boolean;
    masterData: masterDataModal;
    error: string;
  };
  clientDetails: clientData | Array<clientData>;
  taskDetails: any;
  closeModal: Function;
  bill: {
    show: boolean;
    edit: boolean;
    billId: null | number;
    isReimbursement: undefined | boolean;
    totalAmount: number;
    tasks?: Array<{
      taskId: number;
      taskCode: string;
      taskType: string;
      Fees: number;
    }>;
  };
  setFormat: Function;
  billDetails?: any;
  getAllBillingDetails: Function;
  getAllTaskList: Function;
  addProformaInvoiceDispatch: Function;
  addReimbursementInvoiceDispatch: Function;
  isReimbursement: boolean | undefined;
  getMasterDataList: Function;
  getClientAdvancePaymentsDispatch: Function;
  clientAdvancePayments: any;
  getAllBillingPendingTaskList: Function;
  isBillingPendingList: boolean;
}

export const BILL_STATUS_OPTIONS = [
  { value: PAYMENT_STATUS["Partially Paid"], name: "Partially Paid" },
  { value: PAYMENT_STATUS["Fully Paid"], name: "Fully Paid" },
];

const AddBill: FC<props> = ({
  getAllTaskList,
  getAllBillingPendingTaskList,
  getAllBillingDetails,
  taskDetails,
  billDetails,
  setFormat,
  bill,
  closeModal,
  clientDetails,
  masterDataList,
  initialValue,
  billDispatch,
  title,
  addProformaInvoiceDispatch,
  addReimbursementInvoiceDispatch,
  isReimbursement,
  getMasterDataList,
  getClientAdvancePaymentsDispatch,
  clientAdvancePayments,
  isBillingPendingList
}) => {
  const [totalAmount, setTotalAmount] = useState(bill.totalAmount);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState<() => void>(() => {});
  const [searchParam] = useSearchParams();
  const searchObj = useMemo(
    () => getSearchParameter(searchParam),
    [searchParam]
  );
  const sendDataChangeWarning = useSendDataChangeWarning();

  const billValidationSchema = object().shape({
    clientId: number().required("Client is requird"),
    firmId: number().required("Firm is required"),
    // taskAmounts: object().test(
    //   "taskAmounts",
    //   "Task amounts are required",
    //   (value) => {
    //     if (!value || Object.keys(value).length === 0) return false;
    //     return true;
    //   }
    // ),
    paidAmount: string().test(
      "paidAmountRequired",
      "Amount paid is required",
      (value) => {
        if (value === "" && bill.edit) return false;
        return true;
      }
    ),
    status: string().test("required", "Status is required", (value) => {
      if (value === "" && bill.edit) return false;
      return true;
    }),
    
  });

  const handleClientChange = (selectedClient: any, setFieldValue: Function) => {
    setFieldValue("clientId", selectedClient.value);
    setFieldValue("clientName", selectedClient.name);

    getClientAdvancePaymentsDispatch(selectedClient.value);
  };

  useEffect(() => { 
    initialValue.clientId && getClientAdvancePaymentsDispatch(initialValue.clientId);
  }, []);

  const calculateTotalWithCustomFees = (values: any) => {
    if (!bill.tasks) return bill.totalAmount;
    
    let newTotal = bill.totalAmount;
    bill.tasks.forEach(task => {
      const customFee = values.taskAmounts?.[task.taskId];
      if (customFee) {
        // Subtract default fee and add custom fee
        newTotal = newTotal - task.Fees + Number(customFee);
      }
    });
    return newTotal;
  };

  const calculateTotalAmountWithTax = (firmId: number, values: any) => {
    let newTotal = calculateTotalWithCustomFees(values);
    const selectedFirm = masterDataList.masterData?.data?.records?.Firms?.find(
      (firm) => firm.Id == firmId
    );

    if (selectedFirm && selectedFirm.HasGST) {
      const taxRate = selectedFirm.TaxRate || 0;
      const taxAmount = (newTotal * taxRate) / 100;
      newTotal += taxAmount;
    }
    setTotalAmount(newTotal);
  };

  const validateTotal = (values: any) => {
    const sumAdvancePayments = values?.AppliedAdvances?.reduce(
      (sum: number, item: any) => sum + (item.value ? Number(item.value) : 0),
      0
    );

    if (sumAdvancePayments > totalAmount) {
      return {
        AppliedAdvances: [
          ...(values.AppliedAdvances || []).map(() => ({
            value: "Total advance payments exceed the total amount!",
          })),
        ],
      };
    }

    return {};
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          ...initialValue,
          taskAmounts: bill.tasks?.reduce((acc, task) => {
            acc[task.taskId] = task.Fees;
            return acc;
          }, {} as Record<number, number>) || {}
        }}
        validationSchema={billValidationSchema}
        validate={validateTotal}
        onSubmit={async (values, { resetForm }) => {
          const appliedAdvances = clientAdvancePayments?.data?.records
            ?.map((payment: any, index: number) => {
              const enteredAmount = values?.AppliedAdvances?.[index]?.value;
              return enteredAmount
                ? { key: payment.Id, value: Number(enteredAmount) }
                : null;
            })
            .filter(Boolean);

          // Convert taskAmounts to the format expected by BE
          const taskAmountsDict = bill.tasks?.reduce((acc, task) => {
            const amount = values.taskAmounts?.[task.taskId] || task.Fees;
            acc[task.taskId] = Number(amount);
            return acc;
          }, {} as Record<number, number>) || {};
          const updatedValues = {
            ...values,
            IsReimbursementInvoice: values.hasOwnProperty(
              "IsReimbursementInvoice"
            )
              ? values.IsReimbursementInvoice
              : isReimbursement ?? true,
            AppliedAdvances: appliedAdvances,
            taskAmounts: taskAmountsDict,
            //TasksIds: bill.tasks?.map(task => task.taskId) || []
          };
          if (bill.edit) {
            await billDispatch(updatedValues, () => {
              (String(billDetails.ClientId) !== String(values.clientId) ||
                String(billDetails.Firm.Id) !== String(values.firmId) ||
                String(billDetails.Discount) !== String(values.discount)) &&
                setFormat({ show: true });
              resetForm();
              getAllBillingDetails(searchObj);
              closeModal();
              sendDataChangeWarning();
            });
          } else if (
            !updatedValues.isProformaInvoice &&
            !updatedValues.IsReimbursementInvoice
          ) {
            // await billDispatch(updatedValues, () => {
            //   setFormat({ show: true });
            //   resetForm();
            //   !isBillingPendingList && getAllTaskList(searchObj);
            //   isBillingPendingList && getAllBillingPendingTaskList(searchObj);
            //   closeModal();
            //   sendDataChangeWarning();
            // });
            setConfirmCallback(() => async () => {
              await billDispatch(updatedValues, () => {
                setFormat({ show: true });
                resetForm();
                !isBillingPendingList && getAllTaskList(searchObj);
                isBillingPendingList && getAllBillingPendingTaskList(searchObj);
                closeModal();
                sendDataChangeWarning();
              });
          });
          setShowConfirmPopup(true);
          } else if (updatedValues.isProformaInvoice && !isReimbursement) {
            await addProformaInvoiceDispatch(updatedValues, () => {
              // setFormat({ show: true });
              resetForm();
              !isBillingPendingList && getAllTaskList(searchObj);
              isBillingPendingList && getAllBillingPendingTaskList(searchObj);
              closeModal();
              // sendDataChangeWarning();
            });
            // } else if (values.IsReimbursementInvoice) {
          } else {
            await addReimbursementInvoiceDispatch(updatedValues, () => {
              resetForm();
              !isBillingPendingList && getAllTaskList(searchObj);
              isBillingPendingList && getAllBillingPendingTaskList(searchObj);
              closeModal();
            });
          }
          //getMasterDataList();
        }}
      >
        {({ values, touched, errors, isSubmitting, setFieldValue }) => {
          // Update total when custom fees change
          const handleFeeChange = (taskId: number, value: string) => {
            setFieldValue(`taskAmounts.${taskId}`, value);
            const newTotal = calculateTotalWithCustomFees({
              ...values,
              taskAmounts: {
                ...values.taskAmounts,
                [taskId]: value
              }
            });
            setTotalAmount(newTotal);
          };

          return (
            <Form noValidate className="form">
              <Modal.Body>
                <div className="">
                  {!bill.edit && (
                    <>
                      <div className="row mb-6">
                        {/* {bill.isReimbursement && <div className="col-form-label fw-semibold fs-4 text-success">
                          Total Amount: {totalAmount}
                        </div>} */}
                        {!isReimbursement && (
                          <>
                            {/* <div className="col-form-label fw-semibold fs-4 text-success">
                              Total Amount: {totalAmount}
                            </div> */}
                            <label className="col-sm-3 col-form-label fw-semibold fs-6 required">
                              Save as
                            </label>
                            <div className="col-sm-9 row">
                              <div className="d-flex flex-row gap-3 col-sm-6">
                                <div className="form-check form-check-custom form-check-solid">
                                  <Field
                                    className="form-check-input"
                                    type="radio"
                                    name="isProformaInvoice"
                                    value={true}
                                    checked={
                                      String(values.isProformaInvoice) ===
                                      "true"
                                      //&&
                                      //String(values.IsReimbursementInvoice) ===
                                      //"false"
                                    }
                                    onChange={() => {
                                      setFieldValue("isProformaInvoice", true);
                                      setFieldValue(
                                        "IsReimbursementInvoice",
                                        false
                                      );
                                    }}
                                    disabled={isSubmitting}
                                  />
                                </div>
                                <div className="col-form-label fw-semibold fs-6">
                                  Proforma Invoice
                                </div>
                              </div>
                              <div className="d-flex flex-row gap-3 col-sm-6">
                                <div className="form-check form-check-custom form-check-solid">
                                  <Field
                                    className="form-check-input"
                                    type="radio"
                                    name="isProformaInvoice"
                                    value={false}
                                    checked={
                                      String(values.isProformaInvoice) ===
                                      "false"
                                      //&&
                                      //String(values.IsReimbursementInvoice) ===
                                      //"false"
                                    }
                                    onChange={() => {
                                      setFieldValue("isProformaInvoice", false);
                                      setFieldValue(
                                        "IsReimbursementInvoice",
                                        false
                                      );
                                    }}
                                    disabled={isSubmitting}
                                  />
                                </div>
                                <div className="col-form-label fw-semibold fs-6">
                                  Final Invoice
                                </div>
                              </div>

                              {/* <div className="d-flex flex-row gap-3 col-sm-6">
                                <div className="form-check form-check-custom form-check-solid">
                                  <Field
                                    className="form-check-input"
                                    type="radio"
                                    name="IsReimbursementInvoice"
                                    value={true}
                                    checked={
                                      String(values.IsReimbursementInvoice) ===
                                      "true"
                                    }
                                    onChange={() => {
                                      setFieldValue(
                                        "IsReimbursementInvoice",
                                        true
                                      );
                                    }}
                                    disabled={isSubmitting}
                                  />
                                </div>
                                <div className="col-form-label fw-semibold fs-6">
                                  Reimbursement
                                </div>
                              </div> */}
                            </div>
                          </>
                        )}
                      </div>
                      <div className="row mb-6">
                        <label className="col-lg-3 col-form-label fw-semibold fs-6 required">
                          Client
                        </label>

                        {Array.isArray(clientDetails) && (
                          <div className="col-lg-9 fv-row">
                            <DropDown
                              className="text-start form-control form-control-lg form-control-solid form-select"
                              placeholder="Select Client"
                              options={clientDetails?.map(
                                (client: clientData) => {
                                  return {
                                    value: Number(client.ClientId),
                                    name: client.ClientName,
                                  };
                                }
                              )}
                              setFieldValue={setFieldValue}
                              name="clientId"
                              currentValue={{ value: values.clientId }}
                              defaultValue={{
                                value: billDetails ? values.clientId : "",
                                name: billDetails ? values.clientName : "",
                              }}
                              onChange={(selectedOption) =>
                                handleClientChange(
                                  selectedOption,
                                  setFieldValue
                                )
                              }
                              disabled={isSubmitting}
                              showSearch={true}
                            />
                            <ErrorMessage
                              name="clientId"
                              component="div"
                              className="errorMsg"
                            />
                          </div>
                        )}
                        {!Array.isArray(clientDetails) && (
                          <div className="col-lg-9 fv-row">
                            <Field
                              type="text"
                              placeholder="Client"
                              className="form-control form-control-lg form-control-solid"
                              value={values.clientName}
                              name="clientId"
                              id="clientId"
                              disabled
                            />
                          </div>
                        )}
                      </div>
                      <div className="row mb-6">
                        <label className="col-lg-3 col-form-label fw-semibold fs-6 required">
                          Firm
                        </label>

                        <div className="col-lg-9 fv-row">
                          <DropDown
                            className={`text-start form-control form-control-lg form-control-solid form-select ${
                              masterDataList.loading
                                ? "display-dropdown-loader"
                                : ""
                            }`}
                            placeholder="Select Firm"
                            options={masterDataList.masterData?.data?.records?.Firms?.map(
                              (firm: { Id: number; Name: string }) => {
                                return {
                                  value: Number(firm.Id),
                                  name: firm.Name,
                                };
                              }
                            )}
                            displayLoader={masterDataList.loading}
                            setFieldValue={(field: any, value: any) => {
                              setFieldValue(field, value);
                              !bill.isReimbursement && calculateTotalAmountWithTax(value, values);
                            }}
                            name="firmId"
                            currentValue={{ value: Number(values.firmId) }}
                            defaultValue={{
                              value: billDetails ? Number(values.firmId) : "",
                              name: billDetails ? values.firmName : "",
                            }}
                            disabled={isSubmitting}
                            showSearch={true}
                          />
                          <ErrorMessage
                            name="firmId"
                            component="div"
                            className="errorMsg"
                          />
                        </div>
                      </div>
                      {!isReimbursement && (
                        <>
                          <div className="row mb-6">
                            <label className="col-lg-3 col-form-label fw-semibold fs-6">
                              Discount
                            </label>

                            <div className="col-lg-9">
                              <Field
                                type="number"
                                placeholder="Discount"
                                className={`form-control form-control-lg form-control-solid
                        ${
                          touched.discount &&
                          errors.discount &&
                          "is-invalid inValidBorder"
                        }`}
                                name="discount"
                                disabled={isSubmitting}
                              />
                              <ErrorMessage
                                name="discount"
                                component="div"
                                className="errorMsg"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {bill.isReimbursement && <div className="row mb-6">
                        <label className="col-lg-3 col-form-label fw-semibold fs-4">
                          Total Amount
                        </label>
                      <div className="col-lg-9 fv-row">
                          <Field
                            type="number"
                            className="form-control"
                            name={`taskAmounts`}
                            placeholder="Enter custom fee"
                            disabled={true}
                            value={totalAmount}
                          />
                        </div>
                      </div>}
                      {bill.tasks && <div className="row mb-6">
                        <div className="table-responsive">
                          <table className="table table-bordered align-middle">
                            <thead>
                              <tr>
                                <th>Task Code</th>
                                <th>Task</th>
                                <th>Default Fee</th>
                                <th>Custom Fee</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bill.tasks?.map(task => (
                                <tr key={task.taskId}>
                                  <td>{task.taskCode}</td>
                                  <td>{task.taskType}</td>
                                  <td>{task.Fees}</td>
                                  <td>
                                    <Field
                                      type="number"
                                      className="form-control form-control-solid"
                                      name={`taskAmounts.${task.taskId}`}
                                      placeholder="Enter custom fee"
                                      disabled={isSubmitting}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFeeChange(task.taskId, e.target.value)}
                                    />
                                    <ErrorMessage
                                      name={`taskAmounts.${task.taskId}`}
                                      component="div"
                                      className="errorMsg"
                                    />
                                  </td>
                                </tr>
                              ))}
                              <tr>
                                <td className={"fs-3"} colSpan={3}>Total Amount</td>
                                <td>
                                  <Field
                                      type="number"
                                      className="form-control"
                                      name={`taskAmounts`}
                                      placeholder="Enter custom fee"
                                      disabled={true}
                                      value={totalAmount}
                                    />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>}
                      {values.clientId != null &&
                        (!values.isProformaInvoice || isReimbursement) &&
                        clientAdvancePayments?.data?.records &&
                        clientAdvancePayments?.data?.records?.length > 0 && (
                          <>
                            <div className="row">
                              <label className="col-lg-5 col-form-label fw-semibold fs-6">
                                Map Advance Payment
                              </label>
                            </div>

                            <div className="row">
                              <div className="col-lg-12">
                                <table className="table table-bordered align-middle">
                                  <thead>
                                    <tr>
                                      <th>Date</th>
                                      <th>Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {clientAdvancePayments?.data?.records?.map(
                                      (payment: any, index: number) => (
                                        <tr key={payment.Id}>
                                          <td>
                                            {payment.TransactionDate
                                              ? convert(payment.TransactionDate)
                                              : "-"}
                                          </td>
                                          <td>{payment.RemainingAmount}</td>
                                          <td>
                                            {/* <input
                                              type="number"
                                              className="form-control"
                                              name={`AppliedAdvances[${index}].value`}
                                              placeholder="Enter amount"
                                              onChange={(e) =>
                                                setFieldValue(
                                                  `AppliedAdvances[${index}].value`,
                                                  e.target.value
                                                )
                                              }
                                            />
                                            <ErrorMessage
                                              name={`AppliedAdvances[${index}].value`}
                                              component="div"
                                              className="errorMsg"
                                            /> */}
                                            <Field
                                              type="number"
                                              className="form-control form-control-solid"
                                              name={`AppliedAdvances.${index}.value`}
                                              placeholder="Enter amount"
                                            />
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>

                                {Array.isArray(errors.AppliedAdvances) &&
                                  errors.AppliedAdvances.some(
                                    (err) => err?.value
                                  ) && (
                                    <div className="errorMsg">
                                      {
                                        errors.AppliedAdvances.find(
                                          (err) => err?.value
                                        )?.value
                                      }
                                    </div>
                                  )}
                              </div>
                            </div>
                          </>
                        )}
                    </>
                  )}
                  {bill.edit && (
                    <>
                      <div className="row mb-6">
                        <label className="col-lg-3 col-form-label fw-semibold fs-6 required">
                          Paid Amount
                        </label>

                        <div className="col-lg-9">
                          <Field
                            type="number"
                            placeholder="Paid Amount"
                            className={`form-control form-control-lg form-control-solid
                            ${
                              touched.paidAmount &&
                              errors.paidAmount &&
                              "is-invalid inValidBorder"
                            }`}
                            name="paidAmount"
                            disabled={isSubmitting}
                          />
                          <ErrorMessage
                            name="paidAmount"
                            component="div"
                            className="errorMsg"
                          />
                        </div>
                      </div>
                      <div className="row mb-6">
                        <label className="col-lg-3 col-form-label fw-semibold fs-6 required">
                          Status
                        </label>

                        <div className="col-lg-9">
                          <DropDown
                            className="text-start form-control form-control-lg form-control-solid form-select"
                            placeholder="Select Status"
                            options={BILL_STATUS_OPTIONS}
                            setFieldValue={setFieldValue}
                            name="paymentStatus"
                            currentValue={{ value: values.paymentStatus }}
                            defaultValue={{
                              value: billDetails ? values.paymentStatus : "",
                              name:
                                billDetails && values.paymentStatus
                                  ? PAYMENT_STATUS[Number(values.paymentStatus)]
                                  : "",
                            }}
                            disabled={isSubmitting}
                            showSearch={true}
                          />
                          <ErrorMessage
                            name="paymentStatus"
                            component="div"
                            className="errorMsg"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="d-flex justify-content-end pt-6 gap-3">
                  <ResetButton
                    name="Cancel"
                    className="btn btn-light btn-active-light-primary"
                    onClickCallback={() => closeModal()}
                  />
                  <SubmitButton
                    isSubmitting={isSubmitting}
                    className="btn btn-primary"
                    name="Submit"
                  />
                </div>
              </Modal.Body>
            </Form>
          );
        }}
      </Formik>
      {showConfirmPopup && (
        <div className="modal-backdrop confirm-backdrop fade show"></div>
      )}
      <ConfirmChangePopup 
        show={showConfirmPopup}
        onHide={() => setShowConfirmPopup(false)}
        onConfirm={async () => {
          setShowConfirmPopup(false);
          await confirmCallback();
        }}
        title="Generate Final Invoice"
        message="You will not be able to edit invoice details after the Final Invoice is generated. By submitting, you are confirming and generating the Final Invoice."
        disableTime={9000}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    clientAdvancePayments: state.clientAdvancePayments.advancePayments,
  };
};

export default connect(mapStateToProps)(AddBill);
